const normalizeComparableValue = value => (value == null || value === '' ? null : String(value));

const valuesAreEquivalent = (left, right, parseCommittedValue, context) => {
  const normalizedLeft = normalizeComparableValue(
    parseCommittedValue ? parseCommittedValue(left, context) : left,
  );
  const normalizedRight = normalizeComparableValue(
    parseCommittedValue ? parseCommittedValue(right, context) : right,
  );

  if (normalizedLeft === normalizedRight) {
    return true;
  }

  if (normalizedLeft != null && normalizedRight != null) {
    const leftNumber = Number(normalizedLeft);
    const rightNumber = Number(normalizedRight);

    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
      return leftNumber === rightNumber;
    }
  }

  return false;
};

const commitRowFromGridCell = updateRow => params => {
  const row = params.node?.data ?? params.data;

  if (row) {
    updateRow({ ...row });
  }
};

const createEditModeCellValueChangedHandler = ({
  updateRow,
  isEditModeActive = () => true,
  parseCommittedValue = newValue => newValue,
} = {}) => ({ data, node, newValue, colDef, oldValue }) => {
  if (!isEditModeActive()) {
    return;
  }

  if (newValue === undefined) {
    return;
  }

  const field = colDef?.field;
  const baseRow = node?.data ?? data;

  if (field == null) {
    updateRow(baseRow);
    return;
  }

  const committedValue = parseCommittedValue(newValue, { data: baseRow, colDef, node });
  const nodeValue = baseRow[field];
  const normalizedOldValue = oldValue == null || oldValue === '' ? null : String(oldValue);
  const normalizedCommittedValue =
    committedValue == null || committedValue === '' ? null : String(committedValue);
  const normalizedNodeValue = nodeValue == null || nodeValue === '' ? null : String(nodeValue);
  const normalizedNewValue = newValue == null || newValue === '' ? null : String(newValue);

  const isMeaningfulEdit =
    normalizedNewValue != null && normalizedCommittedValue !== normalizedOldValue;

  if (isMeaningfulEdit) {
    updateRow({ ...baseRow, [field]: committedValue });
    return;
  }

  const isSpuriousEmptyEvent = normalizedNewValue == null;

  if (
    isSpuriousEmptyEvent &&
    normalizedNodeValue != null &&
    normalizedNodeValue !== normalizedCommittedValue
  ) {
    updateRow({ ...baseRow, [field]: nodeValue });
    return;
  }

  if (isSpuriousEmptyEvent) {
    return;
  }

  updateRow({ ...baseRow, [field]: committedValue });
};

const createEditModeCellEditingStoppedHandler = ({
  updateRow,
  isEditModeActive = () => true,
  parseCommittedValue = newValue => newValue,
} = {}) => params => {
  if (!isEditModeActive()) {
    return;
  }

  const { data, node, oldValue, colDef } = params;
  const field = colDef?.field;
  const baseRow = node?.data ?? data;
  const parseContext = { data: baseRow, colDef, node };
  const rawValue =
    params.newValue ?? params.value ?? (field != null ? baseRow?.[field] : undefined);

  if (field == null) {
    updateRow(baseRow);
    return;
  }

  if (rawValue === undefined) {
    commitRowFromGridCell(updateRow)(params);
    return;
  }

  const committedValue = parseCommittedValue(rawValue, parseContext);

  if (valuesAreEquivalent(oldValue, committedValue, parseCommittedValue, parseContext)) {
    commitRowFromGridCell(updateRow)(params);
    return;
  }

  updateRow({ ...baseRow, [field]: committedValue });
};

const createEditModeCellEditHandlers = ({
  updateRow,
  isEditModeActive = () => true,
  parseCommittedValue = newValue => newValue,
  commitOnValueChanged = true,
  commitStrategy = 'row',
} = {}) => {
  const commitIfActive = params => {
    if (!isEditModeActive()) {
      return;
    }

    commitRowFromGridCell(updateRow)(params);
  };

  if (commitStrategy === 'row') {
    return {
      ...(commitOnValueChanged && {
        onCellValueChanged: commitIfActive,
      }),
      onCellEditingStopped: commitIfActive,
    };
  }

  const handlerOptions = { updateRow, parseCommittedValue, isEditModeActive };

  return {
    ...(commitOnValueChanged && {
      onCellValueChanged: createEditModeCellValueChangedHandler(handlerOptions),
    }),
    onCellEditingStopped: createEditModeCellEditingStoppedHandler(handlerOptions),
  };
};

export default createEditModeCellEditHandlers;
export {
  commitRowFromGridCell,
  createEditModeCellValueChangedHandler,
  createEditModeCellEditingStoppedHandler,
};
